type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

const pad2 = (value: number) => value.toString().padStart(2, '0');

/**
 * Format a group's last message time for the group list (same rules as iDigestApp).
 * @param timestamp seconds since epoch
 */
export function formatMessageTime(timestamp: number | null | undefined, t: TranslateFn): string {
  if (typeof timestamp !== 'number' || !Number.isFinite(timestamp) || timestamp <= 0) {
    return '';
  }

  const now = Date.now();
  const messageTime = timestamp * 1000;
  const diffMs = now - messageTime;
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);

  const messageDate = new Date(messageTime);
  const nowDate = new Date(now);
  const hhmm = `${pad2(messageDate.getHours())}:${pad2(messageDate.getMinutes())}`;

  if (diffMinutes < 1) {
    return t('JustNow');
  }

  if (diffMinutes < 60) {
    return t('MinutesAgo', { count: diffMinutes });
  }

  if (diffHours < 24) {
    return hhmm;
  }

  const yesterday = new Date(nowDate);
  yesterday.setDate(yesterday.getDate() - 1);
  if (
    messageDate.getDate() === yesterday.getDate() &&
    messageDate.getMonth() === yesterday.getMonth() &&
    messageDate.getFullYear() === yesterday.getFullYear()
  ) {
    return `${t('Yesterday')} ${hhmm}`;
  }

  const monthDay = `${pad2(messageDate.getMonth() + 1)}-${pad2(messageDate.getDate())}`;
  if (messageDate.getFullYear() === nowDate.getFullYear()) {
    return monthDay;
  }

  return `${messageDate.getFullYear()}-${monthDay}`;
}

/**
 * True when the last message is older than either the user-wide or the per-group hide-messages window (minutes),
 * so its time should not show.
 */
export function isMessageExpired(
  lastMessageTime: number | null | undefined,
  userHideMessages?: number,
  groupHideMessages?: number
): boolean {
  if (!lastMessageTime || lastMessageTime <= 0) {
    return true;
  }
  const now = Math.floor(Date.now() / 1000);
  return [userHideMessages, groupHideMessages].some(
    (minutes) => typeof minutes === 'number' && minutes > 0 && lastMessageTime <= now - minutes * 60
  );
}
